// Which map an offline match runs on: the one baked into the build, a built-in map from the repo's maps/
// (read-only; also the editor's templates) or one of the player's own (localMaps.ts). Online always runs
// the baked map — the server's.
import chapel from '../../maps/ruined-chapel.json';
import showcase from '../../maps/showcase.json';
import tworivers from '../../maps/tworivers.json';
import inkshape from '../../maps/inkshape.json';
import millFord from '../../maps/mill-ford.json';
import stonePass from '../../maps/stone-pass.json';
import wolfThicket from '../../maps/wolf-thicket.json';
import haymaking from '../../maps/haymaking.json';
import burntTown from '../../maps/burnt-town.json';
import { ACTIVE_MAP as BAKED_MAP, MAP_HASH as BAKED_MAP_HASH } from '../core/map/active.js';
import { hashMap } from '../core/map/hash.js';
import type { MapData } from '../core/map/schema.js';
import { checkedMap, type LocalMaps, type Result } from './localMaps.js';
import { t } from './i18n/index.js';
import { mapName } from './i18n/names.js';

/** Every file in maps/, by file name (a test keeps this in step with the directory). */
export const BUILTIN_MAPS: Readonly<Record<string, unknown>> = {
  'ruined-chapel': chapel, showcase, tworivers, inkshape,
  'mill-ford': millFord, 'stone-pass': stonePass, 'wolf-thicket': wolfThicket, haymaking, 'burnt-town': burntTown,
};

export const builtinMap = (name: string): Result<MapData> =>
  Object.prototype.hasOwnProperty.call(BUILTIN_MAPS, name) ? checkedMap(BUILTIN_MAPS[name]) : { ok: false, issues: [{ path: '', code: 'notFound', params: { name } }] };

/** 'baked' | 'builtin:<file>' | 'local:<name>' */
export type MapChoiceId = string;
export const BAKED_CHOICE: MapChoiceId = 'baked';
export type MapChoice = { id: MapChoiceId; label: string; group: string };

/** The baked map first (its own file is not listed twice), then the other built-ins, then the player's. */
export function mapChoices(local: LocalMaps): MapChoice[] {
  return [
    { id: BAKED_CHOICE, label: t('maps.baked', { name: mapName(BAKED_MAP.name) }), group: t('maps.builtin') },
    ...Object.keys(BUILTIN_MAPS).flatMap((file): MapChoice[] => {
      const r = builtinMap(file);
      return r.ok && hashMap(r.value) !== BAKED_MAP_HASH ? [{ id: `builtin:${file}`, label: mapName(r.value.name), group: t('maps.builtin') }] : [];
    }),
    ...local.list().map((e): MapChoice => ({ id: `local:${e.name}`, label: e.name, group: t('maps.mine') })),
  ];
}

export function resolveMapChoice(id: MapChoiceId, local: LocalMaps): Result<MapData> {
  if (id === BAKED_CHOICE) return { ok: true, value: BAKED_MAP };
  if (id.startsWith('builtin:')) return builtinMap(id.slice('builtin:'.length));
  if (id.startsWith('local:')) return local.load(id.slice('local:'.length));
  return { ok: false, issues: [{ path: '', code: 'notFound', params: { name: id } }] };
}
